// Parse free-form goal text into individual goal strings.
//
// The user can:
//   - type one goal per line (newline-separated list), OR
//   - dictate / paste a natural-language sentence like "I want to ship the
//     iOS app, hit 50 users, and run a 5k"
//
// We use a hybrid:
//   1. If the text already looks like a list (multiple newlines or bullets),
//      we trust the client-side split.
//   2. Otherwise we ask Claude to extract the discrete goals AND tighten
//      each one into a short, actionable phrase. This is the path Noah
//      cared about: turning prose into proper goals instead of pasting the
//      whole sentence into a single row.

import { callLLM } from './llm.js';

const MAX_LINE_LEN = 70;

// Cheap split: newlines, bullets, OR explicit "1) 2)" / "1. 2." numbering.
// Strips list markers and trims.
function clientSplit(raw) {
  return raw
    .split(/\r?\n+/)
    .map(l => l.replace(/^[\s•\-\*•]+/, '').replace(/^\d+[\.\)]\s*/, '').trim())
    .filter(Boolean);
}

// Heuristic: does this look like a list the user explicitly typed (multi-
// line, all short)? If so, use it as-is. Otherwise treat it as prose.
function looksLikeTypedList(lines) {
  if (lines.length < 2) return false;
  // If every line is short and self-contained, treat it as a typed list.
  return lines.every(l => l.length <= MAX_LINE_LEN);
}

// Ask Claude to extract concrete goals from prose. The prompt is tight on
// purpose: we don't want Claude inventing goals the user didn't say, and
// we want short, action-first phrasing (e.g. "Run a 5K" rather than "I
// want to run a 5K this year").
async function llmExtract(raw, kind /* 'monthly' | 'yearly' | undefined */) {
  const horizon = kind === 'yearly'
    ? 'a yearly goal list'
    : (kind === 'monthly' ? 'a monthly goal list' : 'a personal goal list');

  const system = [
    `You extract discrete, action-first goals from a user's free-form description for ${horizon}.`,
    'Rules:',
    '- Output ONLY a JSON array of strings. No prose, no code fences.',
    '- One goal per array element. Tighten each to a short, action-first phrase (under 12 words).',
    '- Strip filler like "I want to", "I would like to", "my goal is to". Start with a verb when possible.',
    '- Preserve the user\'s intent and any numbers (e.g. "Run a 5K", "Reach 50 users", "Read 12 books").',
    '- DO NOT invent goals the user did not state. If they said one goal, return one.',
    '- DO NOT add explanations.',
    'Examples:',
    'INPUT: "I want to ship the iOS app, hit 50 users, and run a 5K."',
    'OUTPUT: ["Ship the iOS app", "Reach 50 users", "Run a 5K"]',
    'INPUT: "Read more, sleep before midnight, and stop doomscrolling."',
    'OUTPUT: ["Read more", "Sleep before midnight", "Stop doomscrolling"]',
    'INPUT: "Just want to be more present with my family this month."',
    'OUTPUT: ["Be more present with family"]',
  ].join('\n');

  const text = await callLLM({ system, messages: [{ role: 'user', content: raw }] });
  const cleaned = (text || '').trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```\s*$/, '')
    .trim();

  try {
    const parsed = JSON.parse(cleaned);
    if (Array.isArray(parsed)) {
      const out = parsed
        .map(s => (typeof s === 'string' ? s.trim() : ''))
        .filter(Boolean);
      if (out.length) return out;
    }
  } catch { /* fall through */ }

  // Last-ditch: split on commas/"and" as a sanity fallback. This is only
  // reached if Claude returns malformed JSON, which the prompt strongly
  // discourages.
  return raw
    .split(/[,;]\s+| and (?=[a-z])/i)
    .map(s => s.trim())
    .filter(Boolean);
}

export async function parseGoals(rawInput, kind) {
  const raw = (rawInput || '').trim();
  if (!raw) return [];

  const lines = clientSplit(raw);

  // Multi-line typed list — trust the user, don't burn an LLM call.
  if (looksLikeTypedList(lines)) return lines;

  // Single-line prose, paragraph, or anything else: let Claude do the
  // heavy lifting. callLLM is consent-gated so the caller should catch
  // AIConsentDeclinedError and fall back to saving the raw text as one
  // goal if the user declines.
  return await llmExtract(raw, kind);
}
